import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../lib/apiBase';

const AuthContext = createContext(null);
const API = API_BASE;

// Security: auth is carried by httpOnly cookies (set by the backend on login).
// The Bearer token fallback lives in sessionStorage (cleared when the tab
// closes, not readable across browser restarts) instead of localStorage so an
// XSS payload has a far smaller window to exfiltrate it. A non-sensitive
// "has session" hint in localStorage tells us whether to probe /me on load.
const TOKEN_KEY = 'homego_admin_token';
const SESSION_FLAG = 'homego_admin_session';

function formatError(detail) {
  if (detail == null) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not-auth, object = authed
  const token = () => sessionStorage.getItem(TOKEN_KEY);
  const hasSessionHint = () => {
    try { return localStorage.getItem(SESSION_FLAG) === '1'; } catch { return false; }
  };

  const authHeader = useCallback(() => {
    const t = token();
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const checkSession = useCallback(async () => {
    // Only probe session if we have a Bearer token or a session hint — prevents
    // 401 noise on every public page view. Cookie auth still works via /me.
    if (!token() && !hasSessionHint()) {
      setUser(false);
      return;
    }
    try {
      const res = await fetch(`${API}/api/admin/me`, {
        credentials: 'include',
        headers: authHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        // Clean up stale session silently
        sessionStorage.removeItem(TOKEN_KEY);
        try { localStorage.removeItem(SESSION_FLAG); } catch { /* noop */ }
        setUser(false);
      }
    } catch (err) {
      console.warn('Auth session check failed:', err);
      setUser(false);
    }
  }, [authHeader]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/api/admin/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(formatError(data.detail));
    if (data.access_token) sessionStorage.setItem(TOKEN_KEY, data.access_token);
    try { localStorage.setItem(SESSION_FLAG, '1'); } catch { /* noop */ }
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/api/admin/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeader()
      });
    } catch (err) {
      // Logout endpoint unreachable — proceed with client-side cleanup anyway.
      // Log so a stuck-session bug is visible in the console for support.
      console.warn('[Auth] Logout endpoint failed; clearing local session:', err);
    }
    sessionStorage.removeItem(TOKEN_KEY);
    try { localStorage.removeItem(SESSION_FLAG); } catch { /* noop */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, authHeader }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
